import React, { useState, useRef, useEffect, useMemo } from 'react';
import Preview from './preview';
import { Layout, Tabs } from 'tea-component';
import { connect, useLocation, useParams } from 'umi';

import { debounce } from 'throttle-debounce';
import ContentBody from '@op/components/ContentBody';
import './styles/markdown-preview.scss';
import './styles/index.scss';
import GPTPointFunc from '@op/operationLog/pages/gpt';
import {
  buriedPagePoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import useReportMenuTab from '../hooks/useReportMenuTab';
import usePageName from '@op/hooks/usePageName';
const { Content, Body } = Layout;

const titleMap: any = {
  3: 'AI Summary: By Language',
  4: "AI Summary: KOLs' Opinions",
};

function GptReport({ gameInfo, wholeGameInfo, listMenu }: any) {
  const location = useLocation();

  const params = location?.query || {};

  useInitBuriedPointInfo({
    buriedPoint: GPTPointFunc(location.pathname),
    triggerPagePoint: false,
  });

  const { menuId, kind, hasReportId } = useParams<{
    menuId: string;
    kind: string;
    hasReportId: string;
  }>();

  const { pageName, sourceType } = usePageName({
    menuId,
    listMenu,
    defaultSourceType: hasReportId ? 'page' : 'menu',
  });

  const {
    tabs,
    loading: tabLoading,
    activeId,
    setActiveId,
  } = useReportMenuTab({
    gameInfo,
    menuId,
    sourceType,
    defaultId: params.tab,
  });

  const contentRef = useRef<HTMLElement>(null);

  const debounceTime = 100;

  const [scrolling, setScrolling] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    buriedPagePoint(undefined, titleMap[kind]);
    setTimeout(() => {
      setLoading(false);
    }, 100);
  }, [kind]);

  const GPTReportElement: React.ReactNode = useMemo(() => {
    if (loading || tabLoading) return null;

    const { ext } = tabs?.find((val) => activeId === val?.id) || {
      ext: { type: 1, kind },
    };
    const { type, kind: localKind } = ext || {};

    return (
      <Preview
        wholeGameInfo={wholeGameInfo}
        gameInfo={gameInfo}
        scrolling={scrolling}
        contentRef={contentRef}
        reportId={localKind}
        reportType={Number(type)} 
      ></Preview>
    );
  }, [
    tabs,
    activeId,
    wholeGameInfo,
    gameInfo,
    scrolling,
    loading,
    tabLoading,
    kind,
  ]);

  if (!kind) return null;

  return (
    <Layout>
      <Body className="markdown-preview-wrapper">
        <Content
          className="dbo-gpt dbo-markdown markdown-preview"
          ref={contentRef}
          onScrollCapture={debounce(100, () => {
            setScrolling(true);
            setTimeout(() => {
              setScrolling(false);
            }, debounceTime);
          })}
        >
          <Content.Header
            title={pageName}
            className={tabs?.length > 0 ? 'contentHead-normal' : ''}
          >
            {tabs?.length > 0 && (
              <div className="header-tab">
                <Tabs
                  ceiling
                  tabs={tabs}
                  animated={false}
                  activeId={activeId}
                  onActive={(tab) => {
                    setLoading(true); 
                    setActiveId(tab.id);
                    setTimeout(() => {
                      setLoading(false);
                    }, 500);
                  }}
                />
              </div>
            )}
          </Content.Header>
          <ContentBody
            noShowTimeZoneTips
            className={`dbo-markdown-preview dbo-markdown-preview-custom`}
          >
            <>{GPTReportElement}</>
          </ContentBody>
        </Content>
      </Body>
    </Layout>
  );
}

export default connect((state: { opinionCommon: any }) => {
  const { opinionCommon } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    listMenu: opinionCommon.listMenu,
  };
})(GptReport);
